import {
  Component,
  OnInit,
  inject,
  signal,
  computed,
  effect
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { PaginatorModule } from 'primeng/paginator';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { TagModule } from 'primeng/tag';
import { DatePickerModule } from 'primeng/datepicker';
import { RadioButtonModule } from 'primeng/radiobutton';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { TextareaModule } from 'primeng/textarea';
import { MessageService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { LoadingOverlayComponent } from '../shared/loading-overlay.component';
import { CambioOrientacionRepository } from 'src/app/domain/service/cambio-orientacion/cambio-orientacion-repository';
import { HttpCambioOrientacionRepository } from 'src/app/infrastructure/adapters/cambio-orientacion/http-cambio-orientacion.repository';
import {
  CambioOrientacion,
  CambioOrientacionFiltro,
  ResultadoOperacion
} from 'src/app/domain/model/cambio-orientacion/cambio-orientacion';
import { OficinaRepository } from 'src/app/domain/service/oficina/oficina-repository';
import { Oficina } from '@model/oficina/oficina';
import {
  BusquedaClienteDialogComponent,
  ClienteSeleccionado
} from '../common/busquedas/busqueda-cliente-dialog.component';
import {
  BusquedaAsesorDialogComponent,
  AsesorFiltro,
  AsesorSeleccionado
} from '../common/busquedas/busqueda-asesor-dialog.component';
import { PersonaRepository } from 'src/app/domain/service/persona/persona-repository';
import {
  Persona,
  getNombreCompleto
} from 'src/app/domain/model/persona/persona';
import { UsuarioStateService } from 'src/app/infrastructure/state/usuario-state.service';
import { CatalogoRepository } from 'src/app/domain/service/catalogo/catalogo-repository';
import { CatalogoOption } from 'src/app/domain/model/catalogo/catalogo';
import {
  onlyNumbersKeypress,
  sanitizeNumbersOnly,
  sanitizeTextarea,
  containsDangerousContent
} from 'src/app/common/input-utils';
import { esTextoSeguro } from 'src/app/common/sanitizer.util';
import { MAX_OBSERVACIONES_ORDEN, PAGE_SIZE_OPTIONS } from 'src/app/common/constants-ejb';
import { cambioOrientacionListadoPreset } from 'src/app/presentation/shared/table-standardization/table-standard-presets';
import { BusquedaPaginadorComponent } from 'src/app/presentation/shared/busquedas/busqueda-paginador.component';
import { ContadorCaracteresDirective } from 'src/app/presentation/shared/contador-caracteres/contador-caracteres.directive';

/** Legacy WidgetsNamesConstants.genericError (handleErrors de RechazarOrdenActivity). */
const MENSAJE_ERROR_GENERICO_LEGACY =
  'Estimado usuario, en estos momentos estamos presentando fallas técnicas. Por favor contacte al administrador.';

/**
 * Componente: Cambios en Orientaciones (Rechazar / Bajar Saldo)
 *
 * Permite buscar orientaciones de servicio que pueden ser rechazadas
 * (sin programaciones) o bajar su saldo (con programaciones parciales).
 *
 * Legacy:
 * - RechazarOrdenViewImpl.java (vista)
 * - RechazarOrdenActivity.java (controlador)
 * - BajarSaldoRechazarViewImpl.java (vista alternativa)
 *
 * @author Sura - Migración GWT a Spring Boot
 */
@Component({
  selector: 'app-cambio-orientacion',
  standalone: true,
  imports: [
    ContadorCaracteresDirective,
    CommonModule,
    FormsModule,
    CardModule,
    ButtonModule,
    DropdownModule,
    InputTextModule,
    TableModule,
    PaginatorModule,
    ToastModule,
    TooltipModule,
    TagModule,
    DatePickerModule,
    RadioButtonModule,
    CheckboxModule,
    DialogModule,
    TextareaModule,
    ConfirmDialogModule,
    LoadingOverlayComponent,
    BusquedaPaginadorComponent,
    BusquedaClienteDialogComponent,
    BusquedaAsesorDialogComponent
  ],
  styleUrls: ['../shared/styles/grid-table.styles.scss'],
  providers: [
    MessageService,
    {
      provide: CambioOrientacionRepository,
      useClass: HttpCambioOrientacionRepository
    }
  ],
  template: `
    <div class="p-4 max-w-full mx-auto flex flex-col gap-4">
      <p-toast></p-toast>
      <p-confirmDialog></p-confirmDialog>
      <app-loading-overlay [blocked]="loading()" />

      <!-- Header -->
      <div class="mb-4">
        <h1 class="text-2xl font-bold text-gray-800">
          Cambios en Orientaciones
        </h1>
        <p class="text-gray-600">
          Rechazar orientraciones o bajar saldo de programaciones parciales
        </p>
      </div>

      <!-- Filtros -->
      <p-card header="Filtros de Búsqueda" class="mb-4">
        <!-- Fila 1: Orientación y Oficina -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <!-- Orientación de Servicio -->
          <div class="field">
            <label
              for="codigoOrden"
              class="block text-sm font-medium text-gray-700 mb-1"
            >
              Orientación de Servicio
            </label>
            <input
              id="codigoOrden"
              type="text"
              maxlength="15"
              inputmode="numeric"
              pattern="[0-9]*"
              pInputText
              [ngModel]="filtro.codigoOrden ?? ''"
              (beforeinput)="onCodigoOrdenBeforeInput($event)"
              (keydown)="onCodigoOrdenKeydown($event)"
              (ngModelChange)="filtro.codigoOrden = sanitizeNumbersOnly($event)"
              (paste)="onCodigoOrdenPaste($event)"
              (drop)="onCodigoOrdenDrop($event)"
              placeholder="Código de Orientación"
              class="w-full"
            />
          </div>

          <!-- Oficina -->
          <div class="field">
            <label
              for="oficina"
              class="block text-sm font-medium text-gray-700 mb-1"
            >
              Oficina
            </label>
            <p-dropdown
              id="oficina"
              [options]="oficinas()"
              [(ngModel)]="filtro.nmconsecutivoOficina"
              optionLabel="dsoficina"
              optionValue="nmconsecutivo"
              placeholder="Seleccione oficina"
              [showClear]="true"
              [filter]="true"
              filterBy="dsoficina"
              class="w-full"
            >
            </p-dropdown>
          </div>
        </div>

        <!-- Fila 2: Consultar por (fechas) -->
        <div class="border rounded p-4 mb-4">
          <div class="font-medium text-sm text-gray-600 mb-3">
            Consultar por
          </div>
          <div class="flex flex-wrap items-center gap-4">
            <!-- Tipo de fecha -->
            <div class="flex items-center gap-4">
              <div class="flex items-center gap-1">
                <p-radioButton
                  name="tipoFiltroFecha"
                  value="APROBACION"
                  [(ngModel)]="filtro.tipoFiltroFecha"
                  (ngModelChange)="onTipoFiltroFechaChange($event)"
                  inputId="tipo-aprobacion"
                />
                <label for="tipo-aprobacion" class="text-sm cursor-pointer"
                  >Fechas de aprobación</label
                >
              </div>
              <div class="flex items-center gap-1">
                <p-radioButton
                  name="tipoFiltroFecha"
                  value="ENTREGA"
                  [(ngModel)]="filtro.tipoFiltroFecha"
                  (ngModelChange)="onTipoFiltroFechaChange($event)"
                  inputId="tipo-entrega"
                />
                <label for="tipo-entrega" class="text-sm cursor-pointer"
                  >Fechas de entrega inicial</label
                >
              </div>
            </div>
            <!-- Fecha Desde -->
            <div class="flex items-center gap-2">
              <label class="text-sm text-gray-600">Desde:</label>
              <p-datepicker
                [(ngModel)]="fechaDesde"
                dateFormat="dd/mm/yy"
                [showIcon]="true"
                [maxDate]="fechaHasta || undefined"
                placeholder="dd/mm/aaaa"
              >
              </p-datepicker>
            </div>
            <!-- Fecha Hasta -->
            <div class="flex items-center gap-2">
              <label class="text-sm text-gray-600">Hasta:</label>
              <p-datepicker
                [(ngModel)]="fechaHasta"
                dateFormat="dd/mm/yy"
                [showIcon]="true"
                [minDate]="fechaDesde || undefined"
                placeholder="dd/mm/aaaa"
              >
              </p-datepicker>
            </div>
          </div>
        </div>

        <!-- Fila 3: Cliente / Beneficiario -->
        <div class="grid grid-cols-1 gap-4 mb-4">
          <div class="field">
            <label class="block text-sm font-medium text-gray-700 mb-1">
              Cliente / Beneficiario
            </label>
            <div class="flex gap-2 items-center border rounded-lg p-2 min-w-0">
              <input
                pInputText
                [value]="dsBeneficiarioCliente || ''"
                class="flex-1 min-w-0"
                placeholder="Seleccione un cliente/beneficiario..."
                readonly
              />
              <button
                type="button"
                pButton
                icon="pi pi-search"
                class="p-button-outlined p-button-sm min-w-[44px] min-h-[44px]"
                severity="primary"
                size="small"
                pTooltip="Buscar cliente/beneficiario"
                tooltipPosition="left"
                (click)="dialogBuscarClienteVisible = true"
              ></button>
              @if (dsBeneficiarioCliente) {
                <button
                  type="button"
                  pButton
                  icon="pi pi-times"
                  class="p-button-outlined p-button-sm min-w-[44px] min-h-[44px]"
                  severity="danger"
                  size="small"
                  pTooltip="Limpiar cliente"
                  tooltipPosition="left"
                  (click)="limpiarCliente()"
                ></button>
              }
            </div>
          </div>
        </div>

        <!-- Fila 4: Consultor -->
        <div class="grid grid-cols-1 gap-4 mb-4">
          <div class="field">
            <label class="block text-sm font-medium text-gray-700 mb-1">
              Consultor
              @if (esUsuarioAsesor()) {
                <span class="text-xs text-blue-600 ml-2">(precargado)</span>
              }
            </label>
            <div
              class="flex flex-col sm:flex-row gap-2 sm:items-center border rounded-lg p-2 min-w-0"
            >
              <p-dropdown
                [options]="tiposIdentificacion()"
                [ngModel]="consultorTipoIdLabel()"
                optionLabel="descripcion"
                optionValue="codigo"
                placeholder="Tipo Doc"
                styleClass="w-full sm:w-28 max-w-full"
                [disabled]="true"
              />
              <input
                pInputText
                [value]="consultorNumIdLabel()"
                placeholder="Número ID"
                class="w-full sm:w-32"
                readonly
              />
              <input
                pInputText
                [value]="consultorNombreLabel()"
                placeholder="Nombre"
                class="w-full sm:flex-1 min-w-0"
                readonly
              />
              <button
                type="button"
                pButton
                icon="pi pi-search"
                class="p-button-outlined p-button-sm min-w-[44px] min-h-[44px]"
                pTooltip="Buscar consultor"
                tooltipPosition="left"
                [disabled]="esUsuarioAsesor()"
                (click)="abrirBusquedaConsultor()"
              ></button>
              @if (consultorSeleccionado()) {
                <button
                  type="button"
                  pButton
                  icon="pi pi-times"
                  class="p-button-outlined p-button-sm min-w-[44px] min-h-[44px]"
                  severity="danger"
                  pTooltip="Limpiar consultor"
                  tooltipPosition="left"
                  [disabled]="esUsuarioAsesor()"
                  (click)="limpiarConsultor()"
                ></button>
              }
            </div>
          </div>
        </div>

        <!-- Botones de búsqueda -->
        <div class="flex flex-col sm:flex-row gap-3 mt-6">
          <button
            type="button"
            pButton
            label="Buscar"
            icon="pi pi-search"
            class="p-button-primary min-h-[44px] w-full sm:w-auto"
            (click)="buscar()"
            [loading]="loading()"
          ></button>
          <button
            type="button"
            pButton
            label="Limpiar"
            icon="pi pi-eraser"
            severity="secondary"
            class="p-button-outlined min-h-[44px] w-full sm:w-auto"
            (click)="limpiarFiltros()"
          ></button>
        </div>
      </p-card>

      <!-- Acciones masivas -->
      @if (ordenesSeleccionadas().length > 0) {
        <p-card class="mb-4">
          <div
            class="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between"
          >
            <span class="text-gray-700">
              <strong>{{ ordenesSeleccionadas().length }}</strong>
              Orientaciones seleccionadas
            </span>
            <div class="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
              <button
                pButton
                type="button"
                label="Rechazar Orientaciones"
                icon="pi pi-times-circle"
                severity="danger"
                class="p-button-outlined w-full sm:w-auto min-h-[44px]"
                (click)="abrirDialogRechazo('RECHAZAR')"
                pTooltip="Solo orientaciones sin programaciones"
              ></button>
              <button
                pButton
                type="button"
                label="Bajar Saldo Orientaciones"
                icon="pi pi-minus-circle"
                severity="info"
                class="p-button-outlined w-full sm:w-auto min-h-[44px]"
                [disabled]="ordenesSeleccionadas().length === 0"
                (click)="abrirDialogRechazo('BAJAR_SALDO')"
                pTooltip="Solo orientaciones con programaciones parciales"
                styleClass="p-button-warning"
              ></button>
            </div>
          </div>
        </p-card>
      }

      <!-- Tabla de resultados (legacy: getTableOc().setVisible(false) si falla bajar saldo) -->
      <p-card [class.hidden]="!tablaVisible()">
        <ng-template pTemplate="header">
          <div
            class="px-4 pt-4 pb-2 flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center"
          >
            <h3 class="text-lg font-semibold text-gray-700">
              Orientaciones encontradas
              @if (totalRegistros() > 0) {
                <span class="text-sm font-normal text-gray-500 ml-2">
                  (Registros del {{ registroInicio() }} hasta el {{ registroFin() }}
                  de {{ totalRegistros() }})
                </span>
              }
            </h3>
          </div>
        </ng-template>

        <div class="md:hidden space-y-3 px-2 pb-2">
          @if (!loading() && ordenes().length === 0) {
            <div class="text-center p-4 text-gray-500">
              <i class="pi pi-inbox text-3xl mb-2"></i>
              <p>
                No se encontraron orientaciones con los filtros especificados
              </p>
            </div>
          }

          @for (orden of ordenes(); track orden.nmconsecutivo ?? $index) {
            <article
              class="rounded-lg border border-gray-200 dark:border-gray-700 p-3 bg-white dark:bg-gray-800"
            >
              <div class="flex items-start justify-between gap-2 mb-2">
                <h3 class="text-sm font-semibold text-blue-600">
                  {{ textoTabla(orden.dsorden) }}
                </h3>
                <span class="text-xs text-gray-500 dark:text-gray-400">{{
                  textoTabla(orden.dsestadoProgramacion)
                }}</span>
              </div>

              <div class="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm mb-3">
                <div>
                  <span class="text-gray-500 dark:text-gray-400">Empresa:</span>
                  <span class="text-gray-800 dark:text-gray-200">{{
                    textoTabla(orden.empresa)
                  }}</span>
                </div>
                <div>
                  <span class="text-gray-500 dark:text-gray-400"
                    >Producto:</span
                  >
                  <span class="text-gray-800 dark:text-gray-200">{{
                    textoTabla(orden.dsproducto)
                  }}</span>
                </div>
                <div>
                  <span class="text-gray-500 dark:text-gray-400">Tarea:</span>
                  <span class="text-gray-800 dark:text-gray-200">{{
                    textoTabla(orden.tarea)
                  }}</span>
                </div>
                <div>
                  <span class="text-gray-500 dark:text-gray-400"
                    >Clasificación:</span
                  >
                  <span class="text-gray-800 dark:text-gray-200">{{
                    textoTabla(orden.dsclasificacion)
                  }}</span>
                </div>
                <div>
                  <span class="text-gray-500 dark:text-gray-400"
                    >Cantidad Pedida:</span
                  >
                  <span class="text-gray-800 dark:text-gray-200">{{
                    textoTabla(orden.cantidadPedida)
                  }}</span>
                </div>
                <div>
                  <span class="text-gray-500 dark:text-gray-400"
                    >Saldo por Programar:</span
                  >
                  <span class="text-gray-800 dark:text-gray-200">{{
                    textoTabla(orden.saldoPorProg)
                  }}</span>
                </div>
                <div>
                  <span class="text-gray-500 dark:text-gray-400">Entrega:</span>
                  <span class="text-gray-800 dark:text-gray-200"
                    >{{ textoTabla(formatLocalDate(orden.feentregaInicial)) }} -
                    {{
                      textoTabla(formatLocalDate(orden.feentregaFinal))
                    }}</span
                  >
                </div>
              </div>

              <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <p-button
                  [label]="
                    esOrdenSeleccionada(orden)
                      ? 'Quitar selección'
                      : 'Seleccionar'
                  "
                  [icon]="
                    esOrdenSeleccionada(orden)
                      ? 'pi pi-check-circle'
                      : 'pi pi-circle'
                  "
                  [outlined]="true"
                  severity="secondary"
                  size="small"
                  styleClass="w-full min-h-[44px]"
                  (onClick)="toggleSeleccionOrden(orden)"
                />
                <p-button
                  label="Ver detalle"
                  icon="pi pi-eye"
                  severity="info"
                  size="small"
                  styleClass="w-full min-h-[44px]"
                  (onClick)="verDetalle(orden)"
                />
              </div>
            </article>
          }
        </div>

        <div class="hidden md:block">
          <div
            role="region"
            [attr.aria-label]="ordenesTableConfig().a11y.tableAriaLabel"
            class="sr-only"
          ></div>
          <p-table
            [resizableColumns]="true"
            columnResizeMode="expand"
            [value]="ordenes()"
            [(selection)]="ordenesSeleccionadasModel"
            [selectionMode]="ordenesTableSelectionMode()"
            [paginator]="false"
            [rows]="rowsPerPage()"
            [rowsPerPageOptions]="[10, 20, 50, 100]"
            [totalRecords]="totalRegistros()"
            [loading]="loading()"
            [scrollable]="true"
            [responsiveLayout]="'scroll'"
            [tableStyle]="{
              'min-width': ordenesTableConfig().responsive.minWidth || '92rem'
            }"
            [styleClass]="ordenesTableStyleClass()"
            [dataKey]="ordenesTableDataKey()"
            (selectionChange)="onSelectionChange($event)"
          >
            <!-- Header -->
            <ng-template pTemplate="header">
              <tr>
                <th pResizableColumn style="width: 4rem">
                  <p-tableHeaderCheckbox></p-tableHeaderCheckbox>
                </th>
                <th pResizableColumn>
                  <span class="grid-header-text" title="Orientacion"
                    >Orientacion</span
                  >
                </th>
                <th pResizableColumn>
                  <span class="grid-header-text" title="Empresa">Empresa</span>
                </th>
                <th pResizableColumn>
                  <span class="grid-header-text" title="Producto"
                    >Producto</span
                  >
                </th>
                <th pResizableColumn>
                  <span class="grid-header-text" title="Tarea">Tarea</span>
                </th>
                <th pResizableColumn>
                  <span class="grid-header-text" title="Clasificación"
                    >Clasificación</span
                  >
                </th>
                <th pResizableColumn>
                  <span class="grid-header-text" title="Cantidad Pedida"
                    >Cantidad Pedida</span
                  >
                </th>
                <th pResizableColumn>
                  <span class="grid-header-text" title="Saldo Por Prog"
                    >Saldo Por Prog</span
                  >
                </th>
                <th pResizableColumn>
                  <span class="grid-header-text" title="Fecha Entrega Ini"
                    >Fecha Entrega Ini</span
                  >
                </th>
                <th pResizableColumn>
                  <span class="grid-header-text" title="Fecha Entrega Fin"
                    >Fecha Entrega Fin</span
                  >
                </th>
                <th pResizableColumn style="width: 10rem">
                  <span class="grid-header-text" title="Detalle">Detalle</span>
                </th>
              </tr>
            </ng-template>

            <!-- Body -->
            <ng-template pTemplate="body" let-orden>
              <tr>
                <td>
                  <p-tableCheckbox [value]="orden"></p-tableCheckbox>
                </td>
                <td>
                  <span
                    class="grid-cell-text font-medium text-blue-600"
                    [attr.title]="tituloTabla(orden.dsorden)"
                    >{{ textoTabla(orden.dsorden) }}</span
                  >
                </td>
                <td>
                  <span
                    class="grid-cell-text"
                    [attr.title]="tituloTabla(orden.empresa)"
                    >{{ textoTabla(orden.empresa) }}</span
                  >
                </td>
                <td>
                  <span
                    class="grid-cell-text"
                    [attr.title]="tituloTabla(orden.dsproducto)"
                    >{{ textoTabla(orden.dsproducto) }}</span
                  >
                </td>
                <td>
                  <span
                    class="grid-cell-text"
                    [attr.title]="tituloTabla(orden.tarea)"
                    >{{ textoTabla(orden.tarea) }}</span
                  >
                </td>
                <td>
                  <span
                    class="grid-cell-text"
                    [attr.title]="tituloTabla(orden.dsclasificacion)"
                    >{{ textoTabla(orden.dsclasificacion) }}</span
                  >
                </td>
                <td class="text-center">
                  <span
                    class="grid-cell-text"
                    [attr.title]="tituloTabla(orden.cantidadPedida)"
                    >{{ textoTabla(orden.cantidadPedida) }}</span
                  >
                </td>
                <td class="text-center">
                  <span
                    class="grid-cell-text"
                    [attr.title]="tituloTabla(orden.saldoPorProg)"
                    >{{ textoTabla(orden.saldoPorProg) }}</span
                  >
                </td>
                <td class="text-center">
                  <span class="grid-cell-text">{{
                    formatLocalDate(orden.feentregaInicial)
                  }}</span>
                </td>
                <td class="text-center">
                  <span class="grid-cell-text">{{
                    formatLocalDate(orden.feentregaFinal)
                  }}</span>
                </td>
                <td>
                  <div class="flex gap-1 justify-center">
                    <p-button
                      icon="pi pi-eye"
                      severity="info"
                      [rounded]="true"
                      [text]="true"
                      styleClass="min-w-[44px] min-h-[44px]"
                      pTooltip="Ver detalle"
                      (click)="verDetalle(orden)"
                    >
                    </p-button>
                  </div>
                </td>
              </tr>
            </ng-template>

            <!-- Empty -->
            <ng-template pTemplate="emptymessage">
              <tr>
                <td colspan="11" class="text-center p-4">
                  <div class="text-gray-500">
                    <i class="pi pi-inbox text-4xl mb-2"></i>
                    <p>
                      No se encontraron orientaciones con los filtros
                      especificados
                    </p>
                  </div>
                </td>
              </tr>
            </ng-template>
          </p-table>
        </div>

        <!-- Paginación keyset (acceso-datos-oracle §7): sin salto a página arbitraria -->
        @if (totalRegistros() > 0) {
          <app-busqueda-paginador
            [currentPage]="currentPageDisplay()"
            [totalPages]="totalPages()"
            [hasPrevious]="hasPrevious()"
            [hasNext]="hasNext()"
            [pageSizeOptions]="pageSizeOptionValues"
            [selectedPageSize]="rowsPerPage()"
            (pageSizeChange)="rowsPerPage.set($event); onPageSizeChange()"
            (first)="irPrimeraPagina()"
            (previous)="irPaginaAnterior()"
            (next)="irPaginaSiguiente()"
            (last)="irUltimaPagina()"
          ></app-busqueda-paginador>
        }
      </p-card>

      <!-- Dialog de motivo -->
      <p-dialog
        [(visible)]="dialogMotivoVisible"
        [header]="dialogTitulo"
        [modal]="true"
        [closable]="!procesandoOperacion()"
        [style]="{ width: '92vw', maxWidth: '30rem' }"
        [contentStyle]="{ 'max-height': '80vh', 'overflow-y': 'auto' }"
        [breakpoints]="{ '768px': '94vw', '640px': '96vw' }"
        (onHide)="cerrarDialogMotivo()"
      >
        <div class="space-y-4">
          @if (ordenesParaOperar.length > 0) {
            <div class="border border-blue-200 rounded p-3">
              <span class="text-sm font-medium text-blue-700"
                >Orientaciones de servicio a procesar:</span
              >
              <p class="text-sm text-blue-600 mt-1">
                {{ ordenesParaOperarLabel }}
              </p>
            </div>
          }

          @if (mensajeResultadoModal()) {
            <div class="border border-amber-200 rounded p-3">
              <p class="text-sm font-medium text-amber-700">
                Resultado del proceso
              </p>
              <p class="text-sm text-amber-600 mt-1">
                {{ mensajeResultadoModal() }}
              </p>
            </div>
          }

          @if (resultadosOperacionModal().length > 0) {
            <div class="border border-gray-200 rounded p-3">
              <p class="text-sm font-medium text-gray-700 mb-2">
                Resultado final por orden
              </p>
              <div class="space-y-2 max-h-48 overflow-auto">
                @for (
                  resultado of resultadosOperacionModal();
                  track resultado.dsorden
                ) {
                  <div
                    class="text-sm"
                    [class.text-green-700]="resultado.exitoso"
                    [class.text-red-700]="!resultado.exitoso"
                  >
                    <span class="font-medium">{{ resultado.dsorden }}</span
                    >:
                    {{ resultado.exitoso ? 'Exitoso' : 'Error' }}
                    @if (resultado.mensaje) {
                      <span> - {{ resultado.mensaje }}</span>
                    }
                  </div>
                }
              </div>
            </div>
          }

          @if (tipoOperacion === 'RECHAZAR') {
            <div class="field">
              <label
                for="motivo"
                class="block text-sm font-medium text-gray-700 mb-1"
              >
                Motivo *
              </label>
              <p-dropdown
                [filter]="true"
                filterBy="descripcion"
                filterPlaceholder="Buscar motivo rechazo..."
                [resetFilterOnHide]="true"
                emptyFilterMessage="Sin coincidencias"
                id="motivo"
                [options]="motivosRechazo()"
                [(ngModel)]="motivoSeleccionado"
                optionLabel="descripcion"
                optionValue="codigo"
                placeholder="Seleccione un motivo"
                class="w-full"
                appendTo="body"
                [autoZIndex]="true"
                [baseZIndex]="10000"
              >
              </p-dropdown>
            </div>
          }

          <div class="field">
            <label
              for="observaciones"
              class="block text-sm font-medium text-gray-700 mb-1"
            >
              Observaciones *
            </label>
            <textarea
              id="observaciones"
              pTextarea
              [(ngModel)]="observaciones"
              [appContadorCaracteres]="maxObservaciones"
              (blur)="pasarObservacionesAMayusculas()"
              rows="3"
              class="w-full uppercase"
              placeholder="Ingrese observaciones"
            >
            </textarea>
          </div>
        </div>

        <ng-template pTemplate="footer">
          <div
            class="flex flex-wrap justify-end sm:justify-start gap-2 sm:gap-3 w-full"
          >
            <button
              pButton
              type="button"
              label="Cancelar"
              icon="pi pi-times"
              severity="secondary"
              class="p-button-outlined min-h-[44px]"
              [disabled]="procesandoOperacion()"
              (click)="cerrarDialogMotivo()"
            ></button>
            <button
              pButton
              type="button"
              label="Procesar"
              icon="pi pi-check"
              severity="primary"
              class="p-button-primary min-h-[44px]"
              [loading]="procesandoOperacion()"
              [disabled]="!puedeConfirmarOperacion() || procesandoOperacion()"
              (click)="confirmarOperacion()"
            ></button>
          </div>
        </ng-template>
      </p-dialog>

      <!-- Dialog de búsqueda de cliente -->
      @if (dialogBuscarClienteVisible) {
        <app-busqueda-cliente-dialog
          [titulo]="'Buscar Cliente / Beneficiario'"
          (onSelect)="onClienteSeleccionado($event)"
          (onClose)="dialogBuscarClienteVisible = false"
        />
      }

      <!-- Dialog de búsqueda de consultor -->
      @if (dialogBuscarConsultorVisible) {
        <app-asesor-search
          [titulo]="'Buscar Consultor'"
          (onSelect)="seleccionarConsultor($event)"
          (onClose)="cerrarBusquedaConsultor()"
        />
      }

      <!-- Dialog de detalle de orden -->
      <p-dialog
        [(visible)]="dialogDetalleVisible"
        header="Detalle de la Orden"
        [modal]="true"
        [style]="{ width: '92vw', maxWidth: '56rem' }"
        [contentStyle]="{ 'max-height': '80vh', 'overflow-y': 'auto' }"
        [breakpoints]="{ '1024px': '90vw', '768px': '94vw', '640px': '98vw' }"
      >
        @if (ordenDetalle) {
          <!-- Legacy ServiceDetailsInterfaceImpl: mismos campos, orden y etiquetas -->
          <div
            class="grid grid-cols-1 sm:grid-cols-2 gap-x-4 sm:gap-x-6 gap-y-3"
          >
            @for (campo of camposDetalle(ordenDetalle); track campo.etiqueta) {
              <div [ngClass]="{ 'sm:col-span-2': campo.anchoCompleto }">
                <span class="text-gray-500">{{ campo.etiqueta }}</span>
                <div class="font-medium break-words min-h-[1.5rem]">
                  {{ campo.valor }}
                </div>
              </div>
            }
          </div>
        }

        <ng-template pTemplate="footer">
          <div
            class="flex flex-wrap justify-end sm:justify-start gap-2 sm:gap-3 w-full"
          >
            <button
              pButton
              type="button"
              label="Cerrar"
              class="p-button-outlined min-h-[44px]"
              icon="pi pi-times"
              severity="secondary"
              (click)="dialogDetalleVisible = false"
            ></button>
          </div>
        </ng-template>
      </p-dialog>
    </div>
  `
})
export class CambioOrientacionComponent implements OnInit {
  private readonly maxCodigoOrdenLength = 15;

  // Servicios
  private readonly repository = inject(CambioOrientacionRepository);
  private readonly oficinaRepository = inject(OficinaRepository);
  private readonly personaRepository = inject(PersonaRepository);
  private readonly usuarioState = inject(UsuarioStateService);
  private readonly catalogoRepository = inject(CatalogoRepository);
  private readonly messageService = inject(MessageService);

  // Estado
  loading = signal(false);
  ordenes = signal<CambioOrientacion[]>([]);
  totalRegistros = signal(0);
  ordenesSeleccionadasModel: CambioOrientacion[] = [];
  ordenesSeleccionadas = signal<CambioOrientacion[]>([]);
  oficinas = signal<Oficina[]>([]);
  private ultimoFiltroBusqueda: Partial<CambioOrientacionFiltro> | null = null;
  private oficinaSesionUsuario: number | null = null;
  private cdpersonaSesionAsesor: number | null = null;

  // Parámetros
  rangoMaximoMeses = signal<number>(3); // Valor por defecto si no se obtiene del parámetro

  // Filtros
  filtro: Partial<CambioOrientacionFiltro> = {
    tipoFiltroFecha: 'APROBACION',
    size: 10
  };
  fechaDesde: Date | null = null;
  fechaHasta: Date | null = null;
  rowsPerPage = signal(10);
  pageSizeOptionValues = PAGE_SIZE_OPTIONS;

  // Paginación keyset/cursor (skill acceso-datos-oracle §7 — nunca OFFSET).
  // currentPage es solo para mostrar "Página X de Y" — con keyset no hay
  // número de página real, se lleva localmente por navegación (0-based).
  currentPage = signal(0);

  readonly registroInicio = computed(() => {
    if (this.totalRegistros() === 0) return 0;
    return this.currentPage() * this.rowsPerPage() + 1;
  });

  readonly registroFin = computed(() =>
    Math.min(
      (this.currentPage() + 1) * this.rowsPerPage(),
      this.totalRegistros()
    )
  );

  readonly ordenesTableConfig = computed(() =>
    cambioOrientacionListadoPreset.createConfig({
      data: this.ordenes() as unknown as Record<string, unknown>[],
      style: {
        tableClassName: 'tabla-cambio-orientacion',
        striped: true,
        gridlines: false,
        resizableColumns: true,
        rowHover: false
      },
      selection: {
        mode: 'multiple',
        selected: this.ordenesSeleccionadasModel as unknown as Record<
          string,
          unknown
        >[],
        dataKey: 'nmconsecutivo'
      },
      pagination: {
        enabled: false,
        mode: 'server',
        pageSize: this.rowsPerPage(),
        pageSizeOptions: [10, 20, 50, 100],
        currentPageReportTemplate:
          'Registros del {first} hasta el {last} de {totalRecords}'
      },
      state: {
        loading: this.loading(),
        emptyMessage:
          'No se encontraron orientaciones con los filtros especificados'
      },
      responsive: {
        mode: 'hybrid',
        minWidth: '92rem',
        scrollHeight: 'auto',
        cardBreakpointPx: 768
      },
      a11y: {
        tableAriaLabel: 'Tabla de orientaciones para cambio de estado',
        announceSelection: true,
        announcePageChanges: true,
        focusFirstActionOnRowSelect: false
      }
    })
  );

  readonly ordenesTableStyleClass = computed(() => {
    const config = this.ordenesTableConfig();
    const classes = ['p-datatable-sm', config.style.tableClassName];
    if (config.style.gridlines !== false) {
      classes.push('p-datatable-gridlines');
    }
    if (config.style.striped !== false) {
      classes.push('p-datatable-striped');
    }
    return classes.join(' ');
  });

  readonly ordenesTableSelectionMode = computed<'single' | 'multiple' | null>(
    () => {
      const mode = this.ordenesTableConfig().selection.mode;
      return mode === 'single' || mode === 'multiple' ? mode : null;
    }
  );

  readonly ordenesTableDataKey = computed(() =>
    String(this.ordenesTableConfig().selection.dataKey || 'nmconsecutivo')
  );

  constructor() {
    // Sincronizar cambios de paginación
    effect(() => {
      this.rowsPerPage();
    });
  }

  // Cliente seleccionado
  dsBeneficiarioCliente: string | null = null;
  nmconsecutivoBeneficiario: number | null = null;

  // Consultor/Asesor
  consultorSeleccionado = signal<Persona | null>(null);
  /** Consecutivo de empleado del asesor elegido en el buscador (null si viene de la sesión). */
  nmconsecutivoConsultor: number | null = null;
  consultoresResultado = signal<Persona[]>([]);
  loadingConsultorBusqueda = signal(false);
  dialogBuscarClienteVisible = false;

  readonly esUsuarioAsesor = this.usuarioState.esAsesor;

  tiposIdentificacion = signal<CatalogoOption[]>([]);
  dialogBuscarConsultorVisible = false;

  // Computed para mostrar información del consultor
  consultorTipoIdLabel = computed(() => {
    const consultor = this.consultorSeleccionado();
    if (!consultor) {
      return '';
    }

    const consultorAny = consultor as Persona & Partial<AsesorSeleccionado>;
    return (
      consultorAny.cdtipo_identificacion ??
      consultorAny.cdtipoIdentificacion ??
      ''
    );
  });

  consultorNumIdLabel = computed(() => {
    const consultor = this.consultorSeleccionado();
    return consultor?.nmidentificacion ?? '';
  });

  consultorNombreLabel = computed(() => {
    const consultor = this.consultorSeleccionado();
    return consultor ? getNombreCompleto(consultor) : '';
  });

  consultorLabel = computed(() => {
    const consultor = this.consultorSeleccionado();
    return consultor ? getNombreCompleto(consultor) : '';
  });

  // Computed para total de páginas (solo informativo — la navegación real es por cursor)
  totalPages = computed(() => {
    const total = this.totalRegistros();
    const size = this.rowsPerPage();
    return total > 0 ? Math.ceil(total / size) : 0;
  });

  currentPageDisplay = computed(() => {
    if (this.totalRegistros() === 0) return 1;
    return this.currentPage() + 1;
  });

  // Cursores keyset devueltos por el backend
  nextCursor = signal<string | null>(null);
  previousCursor = signal<string | null>(null);
  hasNext = signal(false);
  hasPrevious = signal(false);

  // Dialog motivo
  dialogMotivoVisible = false;
  dialogTitulo = '';
  tipoOperacion: 'RECHAZAR' | 'BAJAR_SALDO' = 'RECHAZAR';
  motivosRechazo = signal<CatalogoOption[]>([]);
  motivoSeleccionado: string | null = null;
  observaciones = '';
  readonly maxObservaciones = MAX_OBSERVACIONES_ORDEN;
  ordenesParaOperar: CambioOrientacion[] = [];
  procesandoOperacion = signal(false);
  /** Legacy RechazarOrdenActivity.executeCommand: la tabla se oculta si bajar saldo falla; buscar la muestra. */
  tablaVisible = signal(true);
  mensajeResultadoModal = signal<string | null>(null);
  resultadosOperacionModal = signal<
    Array<{ dsorden: string; exitoso: boolean; mensaje: string }>
  >([]);

  get ordenesParaOperarLabel(): string {
    return this.ordenesParaOperar.map((o) => o.dsorden).join(', ');
  }


  // Dialog detalle
  dialogDetalleVisible = false;
  ordenDetalle: CambioOrientacion | null = null;

  // Computed
  puedenRechazarSeleccionadas = computed(() => {
    return this.ordenesSeleccionadas().every((o) => o.puedeRechazar);
  });

  puedenBajarSaldoSeleccionadas = computed(() => {
    return this.ordenesSeleccionadas().every((o) => o.puedeBajarSaldo);
  });

  hayOrdenesValidasParaBajarSaldoSeleccionadas = computed(() => {
    return this.ordenesSeleccionadas().some((o) => o.puedeBajarSaldo);
  });

  ngOnInit(): void {
    void this.inicializarComponente();
  }

  private async inicializarComponente(): Promise<void> {
    void this.cargarTiposIdentificacion();
    void this.cargarMotivosRechazo();
    await this.cargarConfiguracion();
    await this.cargarOficinas();
    await this.preseleccionarOficinaUsuario();
  }

  private manejarErrorComponente(
    contexto: string,
    error: unknown,
    opciones?: { mostrarToast?: boolean; mensajeUsuario?: string }
  ): void {
    // Punto único para futura telemetría/auditoría sin exponer detalles técnicos en consola.
    void contexto;
    void error;

    if (opciones?.mostrarToast) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail:
          opciones.mensajeUsuario ??
          'Ocurrió un error inesperado. Intente nuevamente.'
      });
    }
  }

  private async cargarMotivosRechazo(): Promise<void> {
    try {
      const motivos = await this.catalogoRepository.getMotivosRechazoOrden();
      this.motivosRechazo.set(motivos);
    } catch (error) {
      this.motivosRechazo.set([]);
      this.manejarErrorComponente('cargarMotivosRechazo', error);
    }
  }

  private async cargarTiposIdentificacion(): Promise<void> {
    try {
      const tipos = await this.catalogoRepository.getTiposIdentificacion();
      this.tiposIdentificacion.set(tipos);
    } catch (error) {
      this.tiposIdentificacion.set([]);
      this.manejarErrorComponente('cargarTiposIdentificacion', error);
    }
  }

  private async cargarOficinas(): Promise<void> {
    try {
      const oficinas = await this.oficinaRepository.obtenerOficinas();
      this.oficinas.set(oficinas);
    } catch (error) {
      this.oficinas.set([]);
      this.manejarErrorComponente('cargarOficinas', error);
    }
  }

  /**
   * Pre-selecciona la oficina del usuario autenticado.
   * Si es Asesor (tipo 'A'), también pre-carga el consultor con sus datos.
   */
  private async preseleccionarOficinaUsuario(): Promise<void> {
    try {
      const usuario = await this.usuarioState.cargar();

      if (!usuario) return;

      // Pre-seleccionar oficina
      if (usuario.nmconsecOficina) {
        this.oficinaSesionUsuario = usuario.nmconsecOficina;
        this.filtro.nmconsecutivoOficina = usuario.nmconsecOficina;
      } else {
        this.oficinaSesionUsuario = null;
      }

      this.cdpersonaSesionAsesor = null;

      // Si es Asesor, pre-cargar consultor con datos del usuario
      if (this.esUsuarioAsesor()) {
        this.cdpersonaSesionAsesor = usuario.cdpersona;
        const persona: Persona = {
          cdpersona: usuario.cdpersona,
          cdtipo_identificacion: usuario.tipoIdentificacion,
          nmidentificacion: usuario.numeroIdentificacion,
          dsnombre1: usuario.primerNombre,
          dsnombre2: usuario.segundoNombre,
          dsapellido1: usuario.primerApellido,
          dsapellido2: usuario.segundoApellido
        };
        this.consultorSeleccionado.set(persona);
      }
    } catch (error) {
      this.manejarErrorComponente('preseleccionarOficinaUsuario', error);
    }
  }

  private async cargarConfiguracion(): Promise<void> {
    try {
      const configuracion = await this.repository.obtenerConfiguracion();
      const meses = Number.parseInt(
        String(configuracion?.rangoDefaultFeapr ?? ''),
        10
      );
      if (!Number.isNaN(meses) && meses > 0) {
        this.rangoMaximoMeses.set(meses);
      }
    } catch (error) {
      this.manejarErrorComponente('cargarConfiguracion', error);
    } finally {
      // Inicializar fechas después de obtener el parámetro
      this.inicializarFechas();
    }
  }

  private inicializarFechas(tipoFiltro?: 'APROBACION' | 'ENTREGA'): void {
    const tipo = tipoFiltro ?? this.filtro.tipoFiltroFecha ?? 'APROBACION';
    const hoy = new Date();

    if (tipo === 'APROBACION') {
      // Fecha aprobación: desde = hoy - X meses, hasta = hoy
      this.fechaHasta = new Date(hoy);
      this.fechaDesde = new Date(hoy);
      this.fechaDesde.setMonth(hoy.getMonth() - this.rangoMaximoMeses());
    } else {
      // Fecha entrega inicial: desde = hoy, hasta = hoy + X meses
      this.fechaDesde = new Date(hoy);
      this.fechaHasta = new Date(hoy);
      this.fechaHasta.setMonth(hoy.getMonth() + this.rangoMaximoMeses());
    }
  }

  /**
   * Maneja el cambio del tipo de filtro de fecha (radio button).
   * Actualiza las fechas por defecto según el tipo seleccionado.
   */
  onTipoFiltroFechaChange(tipoFiltro: 'APROBACION' | 'ENTREGA'): void {
    this.inicializarFechas(tipoFiltro);
  }

  /**
   * Wrapper para onlyNumbersKeypress utility.
   * Bloquea teclas que no sean números en input number.
   */
  onlyNumbersKeypress(event: KeyboardEvent): void {
    onlyNumbersKeypress(event);
  }

  onCodigoOrdenKeydown(event: KeyboardEvent): void {
    if (!onlyNumbersKeypress(event)) {
      event.preventDefault();
    }
  }

  onCodigoOrdenBeforeInput(event: InputEvent): void {
    // Bloquea inserciones no numéricas (IME, autocomplete, etc.) antes de mutar el input.
    if (
      event.inputType.startsWith('insert') &&
      event.data &&
      /\D/.test(event.data)
    ) {
      event.preventDefault();
    }
  }

  /**
   * Wrapper para sanitizeNumbersOnly utility.
   * Remueve caracteres no numéricos de una cadena.
   */
  sanitizeNumbersOnly(value: string | null | undefined): string {
    return sanitizeNumbersOnly(value ?? '').slice(0, this.maxCodigoOrdenLength);
  }

  onCodigoOrdenPaste(event: ClipboardEvent): void {
    event.preventDefault();

    const input = event.target as HTMLInputElement | null;
    const pastedText = event.clipboardData?.getData('text') ?? '';

    if (!input) {
      this.filtro.codigoOrden = this.sanitizeNumbersOnly(pastedText);
      return;
    }

    const start = input.selectionStart ?? input.value.length;
    const end = input.selectionEnd ?? input.value.length;
    const rawValue = input.value;
    const mergedValue =
      rawValue.slice(0, start) + pastedText + rawValue.slice(end);

    this.filtro.codigoOrden = this.sanitizeNumbersOnly(mergedValue);
  }

  onCodigoOrdenDrop(event: DragEvent): void {
    event.preventDefault();
  }

  async buscar(): Promise<void> {
    // Legacy RechazarOrdenViewImpl.onClickBuscar: validateRequiredFields(panelFiltros)
    // exige Oficina, Desde y Hasta siempre, también al buscar por orientación.
    const camposFaltantes = this.camposObligatoriosFaltantes();
    if (camposFaltantes.length > 0) {
      this.messageService.add({
        severity: 'error',
        summary: 'Filtros requeridos',
        detail: [
          'Por favor ingrese correctamente los siguientes campos',
          ...camposFaltantes
        ].join('\n')
      });
      return;
    }
    const fechaDesde = this.fechaDesde as Date;
    const fechaHasta = this.fechaHasta as Date;

    // Legacy validarFechas: desde <= hasta y hasta no posterior a desde + RANGO_DEFAULT_FEAPR meses
    if (fechaDesde > fechaHasta) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Fechas inválidas',
        detail: 'La fecha final esperada debe ser mayor que la fecha inicial'
      });
      return;
    }

    const rangoMeses = this.rangoMaximoMeses();
    const fechaLimite = new Date(fechaDesde);
    fechaLimite.setMonth(fechaLimite.getMonth() + rangoMeses);
    if (fechaLimite < fechaHasta) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Rango de fechas excedido',
        detail: `La diferencia en meses entre las dos fechas debe ser menor a${rangoMeses}`
      });
      return;
    }

    this.loading.set(true);
    try {
      const consultor = this.consultorSeleccionado();
      const cdpersonaAsesorFiltro = this.esUsuarioAsesor()
        ? (consultor?.cdpersona ?? this.cdpersonaSesionAsesor ?? undefined)
        : consultor?.cdpersona;

      // Con orientación el backend solo filtra por ella, sin asesor (legacy getDTO), pero
      // oficina y fechas viajan igual porque también ahí son obligatorias.
      const camposObligatorios = {
        nmconsecutivoOficina: this.filtro.nmconsecutivoOficina,
        tipoFiltroFecha: this.filtro.tipoFiltroFecha,
        fechaDesde: this.formatDate(fechaDesde),
        fechaHasta: this.formatDate(fechaHasta)
      };
      this.ultimoFiltroBusqueda = this.filtro.codigoOrden
        ? {
            codigoOrden: this.filtro.codigoOrden,
            ...camposObligatorios
          }
        : {
            ...this.filtro,
            cdpersonaAsesor: cdpersonaAsesorFiltro,
            // Legacy getWhereClauses: asesor por consecutivo de empleado y por persona (DNI)
            nmconsecutivoAsesor: this.nmconsecutivoConsultor ?? undefined,
            nmconsecutivoBeneficiario:
              this.nmconsecutivoBeneficiario ?? undefined,
            ...camposObligatorios
          };

      this.tablaVisible.set(true);
      await this.cargarPagina(0, null, 'NEXT', true);
    } catch (error) {
      this.mostrarErrorGenerico('buscarOrientaciones', error);
    } finally {
      this.loading.set(false);
    }
  }

  // Paginación keyset (acceso-datos-oracle §7): primera, anterior, siguiente y última.
  async irPrimeraPagina(): Promise<void> {
    if (this.loading() || this.currentPage() <= 0) return;
    await this.navegar(0, null, 'NEXT');
  }

  async irPaginaAnterior(): Promise<void> {
    if (this.loading() || !this.hasPrevious()) return;
    await this.navegar(this.currentPage() - 1, this.previousCursor(), 'PREVIOUS');
  }

  async irPaginaSiguiente(): Promise<void> {
    if (this.loading() || !this.hasNext()) return;
    await this.navegar(this.currentPage() + 1, this.nextCursor(), 'NEXT');
  }

  /**
   * Salta a la última página. Soportado con Keyset/Cursor porque el
   * backend acepta direction=PREVIOUS sin cursor para este endpoint (ver
   * contrato-paginacion-keyset-lote4.md, apéndice "Lote 4 completado").
   */
  async irUltimaPagina(): Promise<void> {
    if (this.loading() || this.totalPages() <= 0) return;
    await this.navegar(this.totalPages() - 1, null, 'PREVIOUS');
  }

  async onPageSizeChange(): Promise<void> {
    if (this.loading()) return;
    await this.navegar(0, null, 'NEXT');
  }

  private async navegar(
    pagina: number,
    cursor: string | null,
    direction: 'NEXT' | 'PREVIOUS'
  ): Promise<void> {
    if (!this.ultimoFiltroBusqueda) return;
    this.loading.set(true);
    try {
      await this.cargarPagina(pagina, cursor, direction, false);
    } catch (error) {
      this.mostrarErrorGenerico('paginarOrientaciones', error);
    } finally {
      this.loading.set(false);
    }
  }

  /**
   * Ejecuta la búsqueda en el backend por cursor (keyset, ver skill
   * acceso-datos-oracle §7). Una sola llamada devuelve content + totalElements.
   */
  private async cargarPagina(
    pagina: number,
    cursor: string | null,
    direction: 'NEXT' | 'PREVIOUS',
    mostrarMensajeSinResultados: boolean
  ): Promise<void> {
    if (!this.ultimoFiltroBusqueda) {
      return;
    }

    const resultado = await this.repository.buscarOrdenes({
      ...this.ultimoFiltroBusqueda,
      cursor,
      direction,
      size: this.rowsPerPage()
    });

    this.ordenes.set(resultado.content);
    this.totalRegistros.set(resultado.totalElements);
    this.nextCursor.set(resultado.nextCursor);
    this.previousCursor.set(resultado.previousCursor);
    this.hasNext.set(resultado.hasNext);
    this.hasPrevious.set(resultado.hasPrevious);
    this.currentPage.set(pagina);
    this.ordenesSeleccionadasModel = [];
    this.ordenesSeleccionadas.set([]);

    // Legacy RechazarOrdenActivity.listarOrdenes: busquedaNoArrojoResultado (Warning)
    if (mostrarMensajeSinResultados && resultado.totalElements === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Sin resultados',
        detail: 'No se recuperó información con los parámetros ingresados.'
      });
    }
  }

  /** Legacy handleErrors / genericError (WidgetsNamesConstants). */
  private mostrarErrorGenerico(operacion: string, error: unknown): void {
    this.manejarErrorComponente(operacion, error, {
      mostrarToast: true,
      mensajeUsuario: MENSAJE_ERROR_GENERICO_LEGACY
    });
  }

  /** Títulos legacy de lbOficina, dfFeApDesde y dfFeApHasta (WidgetsNamesConstants). */
  private camposObligatoriosFaltantes(): string[] {
    const faltantes: string[] = [];
    if (!this.filtro.nmconsecutivoOficina) {
      faltantes.push('Seleccione la oficina');
    }
    if (!this.fechaDesde) {
      faltantes.push('Fecha inicial (aaaa/mm/dd)');
    }
    if (!this.fechaHasta) {
      faltantes.push('Fecha final (aaaa/mm/dd)');
    }
    return faltantes;
  }

  private formatDate(date: Date): string {
    return date.toISOString().split('T')[0];
  }

  /**
   * Formatea una fecha ISO (YYYY-MM-DD) como fecha local dd/MM/yyyy.
   * Evita el desfase de un día que causa el pipe `date` al interpretar la cadena como UTC.
   */
  formatLocalDate(dateStr: string | undefined | null): string {
    if (!dateStr) return '-';
    try {
      const parts = String(dateStr).split('T')[0].split('-');
      const d = new Date(
        Number(parts[0]),
        Number(parts[1]) - 1,
        Number(parts[2])
      );
      return d.toLocaleDateString('es-CO', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (error) {
      this.manejarErrorComponente('formatLocalDate', error);
      return String(dateStr);
    }
  }

  textoTabla(valor: unknown): string {
    const texto = String(valor ?? '').trim();
    return texto || '-';
  }

  tituloTabla(valor: unknown): string {
    return String(valor ?? '').trim();
  }

  /**
   * Limpia filtros, resultados y cursores.
   */
  limpiarFiltros(): void {
    this.filtro = {
      tipoFiltroFecha: 'APROBACION',
      nmconsecutivoOficina: this.oficinaSesionUsuario ?? undefined,
      size: 10
    };
    this.inicializarFechas();
    this.ordenes.set([]);
    this.totalRegistros.set(0);
    this.currentPage.set(0);
    this.nextCursor.set(null);
    this.previousCursor.set(null);
    this.hasNext.set(false);
    this.hasPrevious.set(false);
    this.ultimoFiltroBusqueda = null;
    this.ordenesSeleccionadasModel = [];
    this.ordenesSeleccionadas.set([]);
    // Limpiar cliente y consultor
    this.dsBeneficiarioCliente = null;
    this.nmconsecutivoBeneficiario = null;
    if (!this.esUsuarioAsesor()) {
      this.nmconsecutivoConsultor = null;
      this.consultorSeleccionado.set(null);
    }
    this.consultoresResultado.set([]);
  }

  // === Handlers de búsqueda de cliente ===
  onClienteSeleccionado(cliente: ClienteSeleccionado): void {
    this.filtro = {
      ...this.filtro,
      nmconsecutivoBeneficiario: cliente.nmconsecutivo
    };
    this.nmconsecutivoBeneficiario = cliente.nmconsecutivo;
    this.dsBeneficiarioCliente = cliente.nombreComercial;
    this.dialogBuscarClienteVisible = false;
  }

  limpiarCliente(): void {
    this.filtro = {
      ...this.filtro,
      nmconsecutivoBeneficiario: undefined
    };
    this.nmconsecutivoBeneficiario = null;
    this.dsBeneficiarioCliente = null;
  }

  // === Handlers de búsqueda de consultor ===
  abrirBusquedaConsultor(): void {
    this.dialogBuscarConsultorVisible = true;
  }

  async buscarConsultor(filtro: AsesorFiltro): Promise<void> {
    this.loadingConsultorBusqueda.set(true);
    try {
      const resultado = await this.personaRepository.getPageWithFilters(
        undefined,
        100,
        filtro
      );
      this.consultoresResultado.set(resultado.content);

      if (resultado.content.length === 0) {
        this.messageService.add({
          severity: 'info',
          summary: 'Sin resultados',
          detail:
            'No se encontraron consultores con los criterios especificados'
        });
      }
    } catch (error) {
      this.consultoresResultado.set([]);
      this.manejarErrorComponente('buscarConsultor', error, {
        mostrarToast: true,
        mensajeUsuario: 'Error al buscar consultores'
      });
    } finally {
      this.loadingConsultorBusqueda.set(false);
    }
  }

  seleccionarConsultor(consultor: AsesorSeleccionado): void {
    this.nmconsecutivoConsultor = consultor.nmconsecutivo ?? null;
    this.consultorSeleccionado.set({
      cdpersona: consultor.cdpersona,
      cdtipo_identificacion: consultor.cdtipoIdentificacion,
      nmidentificacion: consultor.nmidentificacion,
      dsnombre1: consultor.dsnombre1,
      dsnombre2: consultor.dsnombre2,
      dsapellido1: consultor.dsapellido1,
      dsapellido2: consultor.dsapellido2,
      dsnombre_comercial: consultor.dsnombreComercial,
      snactivo: consultor.snactivo
    });
    this.consultoresResultado.set([]);
    this.dialogBuscarConsultorVisible = false;
  }

  onConsultorSeleccionado(consultor: Persona | AsesorSeleccionado): void {
    if ('cdtipoIdentificacion' in consultor) {
      this.nmconsecutivoConsultor = consultor.nmconsecutivo ?? null;
      this.consultorSeleccionado.set(consultor as unknown as Persona);
      this.consultoresResultado.set([]);
      this.dialogBuscarConsultorVisible = false;
      return;
    }

    this.nmconsecutivoConsultor = null;
    this.consultorSeleccionado.set(consultor);
    this.consultoresResultado.set([]);
    this.dialogBuscarConsultorVisible = false;
  }

  limpiarBusquedaConsultor(): void {
    this.consultoresResultado.set([]);
  }

  cerrarBusquedaConsultor(): void {
    this.dialogBuscarConsultorVisible = false;
    this.consultoresResultado.set([]);
  }

  limpiarConsultor(): void {
    this.nmconsecutivoConsultor = null;
    this.consultorSeleccionado.set(null);
    this.consultoresResultado.set([]);
  }

  // Expose helper
  getNombreCompleto = getNombreCompleto;

  onSelectionChange(selection: CambioOrientacion[]): void {
    this.ordenesSeleccionadas.set(selection);
  }

  esOrdenSeleccionada(orden: CambioOrientacion): boolean {
    return this.ordenesSeleccionadasModel.some(
      (o) => o.nmconsecutivo === orden.nmconsecutivo
    );
  }

  toggleSeleccionOrden(orden: CambioOrientacion): void {
    const yaSeleccionada = this.esOrdenSeleccionada(orden);
    if (yaSeleccionada) {
      this.ordenesSeleccionadasModel = this.ordenesSeleccionadasModel.filter(
        (o) => o.nmconsecutivo !== orden.nmconsecutivo
      );
    } else {
      this.ordenesSeleccionadasModel = [
        ...this.ordenesSeleccionadasModel,
        orden
      ];
    }
    this.ordenesSeleccionadas.set([...this.ordenesSeleccionadasModel]);
  }

  // === Ver Detalle ===
  /**
   * Campos del detalle en el orden y con las etiquetas de ServiceDetailsInterfaceImpl (legacy).
   * Un dato faltante queda vacío, como el try/catch por campo del legacy. Cantidad programada,
   * saldo por programar y saldo por ejecutar quedan siempre vacíos: RechazarOrdenActivity.goToDetalle
   * llama refresh(dto, false) y ServiceDetailsInterfaceImpl.reset no tiene esos valores (NPE ignorado),
   * también cuando el detalle cae al dato de la grilla.
   */
  camposDetalle(
    orden: CambioOrientacion
  ): { etiqueta: string; valor: string; anchoCompleto?: boolean }[] {
    const texto = (valor: unknown): string =>
      valor === null || valor === undefined ? '' : String(valor);
    const fecha = (valor?: string): string =>
      valor ? String(valor).split('T')[0].replaceAll('-', '/') : '';
    return [
      { etiqueta: 'Aseguradora', valor: texto(orden.dsaseguradora) },
      { etiqueta: 'Cliente', valor: texto(orden.dscliente) },
      { etiqueta: 'Orientación de Servicio', valor: texto(orden.codigoOrientacion) },
      { etiqueta: 'Unidad Administrativa', valor: texto(orden.dsunidadAdministrativa) },
      { etiqueta: 'Producto', valor: texto(orden.dsproducto) },
      { etiqueta: 'Clasificación', valor: texto(orden.dsclasificacion) },
      { etiqueta: 'Tarea', valor: texto(orden.tarea) },
      { etiqueta: 'Servicio CGR', valor: texto(orden.servicioCgr) },
      { etiqueta: 'Requiere informe', valor: texto(orden.snrequiereInforme) },
      { etiqueta: 'Fecha inicial de entrega (aaaa/mm/dd)', valor: fecha(orden.feentregaInicial) },
      { etiqueta: 'Fecha final de entrega (aaaa/mm/dd)', valor: fecha(orden.feentregaFinal) },
      { etiqueta: 'Cantidad pedida', valor: texto(orden.cantidadPedida) },
      { etiqueta: 'Cantidad programada', valor: '' },
      { etiqueta: 'Saldo por programar', valor: '' },
      { etiqueta: 'Saldo por ejecutar', valor: '' },
      { etiqueta: 'Estado de la programación', valor: texto(orden.dsestadoProgramacion) },
      { etiqueta: 'Profesional', valor: texto(orden.dsprofesional) },
      { etiqueta: 'Consultor sugerido', valor: texto(orden.dsconsultorSugerido) },
      { etiqueta: 'Nombre Contacto', valor: texto(orden.nombreContacto) },
      { etiqueta: 'Teléfono del Contacto', valor: texto(orden.telefonoContacto) },
      { etiqueta: 'Dirección de entrega', valor: texto(orden.dsdireccionEntrega) },
      { etiqueta: 'Municipio de entrega', valor: texto(orden.dsmunicipioEntrega) },
      { etiqueta: 'Centro de trabajo', valor: texto(orden.dscentroTrabajo) },
      { etiqueta: 'Localidad', valor: texto(orden.dslocalidad) },
      { etiqueta: 'Barrio', valor: texto(orden.dsbarrio) },
      { etiqueta: 'Observación', valor: texto(orden.dsobservaciones), anchoCompleto: true }
    ];
  }

  async verDetalle(orden: CambioOrientacion): Promise<void> {
    // Obtener detalle completo desde el backend usando query DETALLE_ORDEN
    try {
      this.loading.set(true);
      const detalle = await this.repository.obtenerDetalle(orden.nmconsecutivo);
      this.ordenDetalle = detalle;
      this.dialogDetalleVisible = true;
    } catch (error) {
      // Fallback: usar datos de la lista si falla la consulta de detalle
      this.ordenDetalle = orden;
      this.dialogDetalleVisible = true;
      this.manejarErrorComponente('verDetalle', error);
    } finally {
      this.loading.set(false);
    }
  }

  // === Operaciones ===
  rechazarOrdenIndividual(orden: CambioOrientacion): void {
    this.ordenesParaOperar = [orden];
    this.abrirDialogRechazo('RECHAZAR');
  }

  bajarSaldoOrdenIndividual(orden: CambioOrientacion): void {
    this.ordenesParaOperar = [orden];
    this.abrirDialogRechazo('BAJAR_SALDO');
  }

  abrirDialogRechazo(tipo: 'RECHAZAR' | 'BAJAR_SALDO'): void {
    this.tipoOperacion = tipo;
    // Legacy RechazarOrdenViewImpl: breadCrumbRechazarOrden / lbPopupEdit.setText("Bajar saldo orden")
    this.dialogTitulo =
      tipo === 'RECHAZAR' ? 'Rechazar Orientaciones' : 'Bajar saldo orden';

    // Siempre tomar la selección actual del grid (salvo operación individual)
    if (this.ordenesParaOperar.length === 0) {
      this.ordenesParaOperar = [...this.ordenesSeleccionadas()];
    } else {
      // Operación individual: ya se asignó en el método que llama
    }

    // Legacy RechazarOrdenViewImpl.onClickSaRechazar / onClickSaBajarSaldo: si alguna
    // orden no cumple, se lista cada una y no se abre el diálogo (no se procesa ninguna).
    const ordenesInvalidas = this.ordenesParaOperar.filter((o) =>
      tipo === 'RECHAZAR' ? !o.puedeRechazar : !o.puedeBajarSaldo
    );
    if (ordenesInvalidas.length > 0) {
      const prefijo =
        tipo === 'RECHAZAR'
          ? 'El estado de la orden no permite la cancelación: '
          : 'El estado de la orden no permite bajar el saldo: ';
      this.messageService.add({
        severity: 'error',
        summary: 'Validación de orientaciones',
        detail: ordenesInvalidas.map((o) => prefijo + o.dsorden).join('\n')
      });
      this.ordenesParaOperar = [];
      return;
    }

    this.motivoSeleccionado = null;
    this.observaciones = '';
    this.mensajeResultadoModal.set(null);
    this.resultadosOperacionModal.set([]);
    this.dialogMotivoVisible = true;
  }

  cerrarDialogMotivo(): void {
    this.dialogMotivoVisible = false;
    this.ordenesParaOperar = [];
    this.motivoSeleccionado = null;
    this.observaciones = '';
    this.procesandoOperacion.set(false);
    this.mensajeResultadoModal.set(null);
    this.resultadosOperacionModal.set([]);
  }

  /** Legacy: el textarea de observaciones convierte a mayúsculas al perder el foco (onChange). */
  pasarObservacionesAMayusculas(): void {
    this.observaciones = this.observaciones.toUpperCase();
  }

  async confirmarOperacion(): Promise<void> {
    if (this.tipoOperacion === 'RECHAZAR' && !this.motivoSeleccionado) {
      return;
    }

    if (!this.observaciones.trim()) {
      return;
    }

    // Legacy (Utilidades.getTextAreaChangeHandler): el texto pasa a mayúsculas. Fuera de eso viaja
    // tal cual (sin trim ni escape HTML) porque se persiste y va a SP_PSC_CANCELAR_ORDEN; el
    // backend lo revalida con @SafeText y vuelve a normalizarlo. El CHECK de PDN
    // T094_ORDEN_TAREAS_CK_DSMOTIVO rechazó una orden por llegar en minúsculas.
    this.pasarObservacionesAMayusculas();
    const observacionesTexto = this.observaciones;
    // El maxlength ya lo impide al escribir; pasar a mayúsculas puede alargar el texto (ß -> SS)
    if (observacionesTexto.length > this.maxObservaciones) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validación',
        detail: `Las observaciones no pueden exceder ${this.maxObservaciones} caracteres`
      });
      return;
    }
    if (!esTextoSeguro(observacionesTexto)) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validación',
        detail: "El campo 'Observaciones' contiene caracteres no permitidos"
      });
      return;
    }

    this.procesandoOperacion.set(true);
    this.mensajeResultadoModal.set(null);
    this.resultadosOperacionModal.set([]);
    this.loading.set(true);

    const esBajarSaldo = this.tipoOperacion === 'BAJAR_SALDO';
    // Sin cantidades: como en el legacy, la regla la valida la pantalla (abrirDialogRechazo).
    const request = {
      ordenes: this.ordenesParaOperar.map((o) => ({
        nmconsecutivo: o.nmconsecutivo,
        dsorden: o.dsorden
      })),
      motivo: esBajarSaldo ? undefined : (this.motivoSeleccionado ?? undefined),
      observaciones: observacionesTexto
    };

    // Legacy RechazarOrdenViewImpl.onClickRechazarOc: el diálogo se cierra al enviar.
    if (esBajarSaldo) {
      this.dialogMotivoVisible = false;
    }

    try {
      if (esBajarSaldo) {
        this.procesarResultadoBajarSaldo(
          await this.repository.bajarSaldoOrdenes(request)
        );
        await this.recargarSiTablaVisible();
        return;
      }
      await this.procesarResultadoOperacion(
        await this.repository.rechazarOrdenes(request)
      );
    } catch (error) {
      if (esBajarSaldo) {
        // Legacy executeCommand.handleErrors: solo genericError, el diálogo ya está cerrado.
        this.mostrarErrorGenerico('confirmarOperacion', error);
        return;
      }
      // El modal queda abierto con las órdenes intactas para poder reintentar. Un corte del
      // lote (excepción del SP) puede dejar órdenes ya procesadas: se refresca el listado.
      this.manejarErrorComponente('confirmarOperacion', error);
      this.mensajeResultadoModal.set(MENSAJE_ERROR_GENERICO_LEGACY);
      this.mostrarErrorGenerico('confirmarOperacion', error);
      await this.recargarListado();
    } finally {
      if (esBajarSaldo) {
        this.ordenesParaOperar = [];
        this.motivoSeleccionado = null;
        this.observaciones = '';
      }
      this.procesandoOperacion.set(false);
      this.loading.set(false);
    }
  }

  /**
   * Rechazar: si todas las órdenes salieron bien se cierra el modal, se recarga el listado y se
   * confirma con un toast. Si alguna falló el modal queda abierto con el detalle por orden y solo
   * con las órdenes pendientes (las ya procesadas no se reenvían); si hubo alguna exitosa el
   * listado se refresca para reflejarla.
   */
  private async procesarResultadoOperacion(
    resultados: ResultadoOperacion[]
  ): Promise<void> {
    const exitosos = resultados.filter((r) => r.exitoso);

    if (resultados.length > 0 && exitosos.length === resultados.length) {
      this.cerrarDialogMotivo();
      this.messageService.add({
        severity: 'success',
        summary: 'Rechazo de orientaciones',
        detail:
          this.unirMensajes(resultados) ||
          'Las orientaciones fueron rechazadas correctamente.',
        life: 5000
      });
      await this.recargarListado();
      return;
    }

    this.resultadosOperacionModal.set(
      resultados.map((r) => ({
        dsorden: r.dsorden ?? String(r.nmconsecutivo),
        exitoso: r.exitoso,
        mensaje: r.mensaje ?? ''
      }))
    );
    this.mensajeResultadoModal.set(
      resultados.length === 0
        ? MENSAJE_ERROR_GENERICO_LEGACY
        : `Proceso finalizado: ${exitosos.length} de ${resultados.length} orden(es) procesada(s) correctamente.`
    );

    if (exitosos.length > 0) {
      const idsExitosos = new Set(exitosos.map((r) => r.nmconsecutivo));
      this.ordenesParaOperar = this.ordenesParaOperar.filter(
        (o) => !idsExitosos.has(o.nmconsecutivo)
      );
      await this.recargarListado();
    }
  }

  /**
   * Bajar saldo, como RechazarOrdenActivity.executeCommand + OrdersHandler.bajarSaldoOrden: si todas
   * salieron bien, mensaje de éxito; si alguna falló, un único mensaje de error con los textos de
   * todas las órdenes en orden (éxitos y errores, separados por salto de línea) y la tabla oculta.
   * Sin texto, genericError.
   */
  private procesarResultadoBajarSaldo(resultados: ResultadoOperacion[]): void {
    const mensajes = this.unirMensajes(resultados);
    if (resultados.every((r) => r.exitoso)) {
      this.messageService.add({
        severity: 'success',
        summary: 'Baja de saldo de orientaciones',
        detail:
          mensajes || 'El saldo de las orientaciones fue bajado correctamente.',
        life: 5000
      });
      return;
    }
    this.messageService.add({
      severity: 'error',
      summary: 'Baja de saldo de orientaciones',
      detail: mensajes || MENSAJE_ERROR_GENERICO_LEGACY,
      life: 5000
    });
    this.tablaVisible.set(false);
    this.ordenesSeleccionadasModel = [];
    this.ordenesSeleccionadas.set([]);
  }

  private unirMensajes(resultados: ResultadoOperacion[]): string {
    return resultados
      .map((r) => (r.mensaje ?? '').trim())
      .filter((m) => m.length > 0)
      .join('\n');
  }

  private async recargarSiTablaVisible(): Promise<void> {
    if (this.tablaVisible()) {
      await this.recargarListado();
    }
  }

  /** Recarga la primera página con el último filtro aplicado; un fallo aquí no revierte la operación. */
  private async recargarListado(): Promise<void> {
    if (!this.ultimoFiltroBusqueda) {
      return;
    }
    try {
      await this.cargarPagina(0, null, 'NEXT', false);
    } catch (error) {
      this.mostrarErrorGenerico('recargarListado', error);
    }
  }

  puedeConfirmarOperacion(): boolean {
    if (!this.observaciones.trim()) {
      return false;
    }
    if (this.observaciones.length > this.maxObservaciones) {
      return false;
    }
    if (this.tipoOperacion === 'RECHAZAR') {
      return !!this.motivoSeleccionado;
    }
    return true;
  }
}
